"use client";

import Link from "next/link";
import { useLocale, useTranslations } from "next-intl";
import { Label } from "@qrody/ui";
import type { AnonymousFormat } from "@qrody/api-client/render";
import { PreviewPane, renderClientPane, renderServerPane } from "../rendering/preview-pane";

export interface PreviewPanelProps {
  compareMode: boolean;
  clientPreview: string | null;
  serverPreview: string | null;
  format: AnonymousFormat;
  pending: boolean;
  errMessage: string | null;
  displayMm: number;
  setDisplayMm: (mm: number) => void;
  matrixMeta: { size: number; version: number; mask: number } | null;
  currentMatrixModules: number;
  ecc: string;
}

/**
 * Centre column (Col 2 on lg+) — the live QR preview pane plus a small
 * caption with the encoder's chosen version + mask + module count + ECC,
 * linked into the help-center article that explains the QR-spec terms.
 */
export function PreviewPanel({
  compareMode,
  clientPreview,
  serverPreview,
  format,
  pending,
  errMessage,
  displayMm,
  setDisplayMm,
  matrixMeta,
  currentMatrixModules,
  ecc,
}: PreviewPanelProps) {
  const t = useTranslations("generator");
  const locale = useLocale();

  return (
    <section
      aria-labelledby="code-heading"
      className="min-w-0 rounded-xl border bg-card p-3 shadow-sm sm:p-4 order-1 md:order-1 md:col-span-2 lg:col-span-1 lg:col-start-2 lg:row-start-1 lg:sticky lg:top-3"
    >
      <h2 id="code-heading" className="sr-only">
        {t("section.code")}
      </h2>
      {/* Display-size slider — sits above the QR so the control reads
          before the artifact it scales. The slider drives
          renderClientPane's CSS sizing only; the encoded file size
          lives next to the file-resolution slider in the design panel. */}
      <div className="mb-3 space-y-2">
        <div className="flex items-baseline justify-between">
          <Label htmlFor="displaySize">{t("field.displaySize")}</Label>
          <span aria-live="polite" className="text-sm tabular-nums text-muted-foreground">
            {displayMm} {t("field.displaySizeUnit")}
          </span>
        </div>
        <input
          id="displaySize"
          type="range"
          min={10}
          max={200}
          step={5}
          value={displayMm}
          onChange={(e) => setDisplayMm(Number(e.target.value))}
          className="h-2 w-full cursor-pointer appearance-none rounded-full bg-muted accent-primary"
          aria-valuemin={10}
          aria-valuemax={200}
          aria-valuenow={displayMm}
        />
        <p className="text-xs text-muted-foreground">{t("field.displaySizeHint")}</p>
      </div>
      {compareMode ? (
        // Two-pane compare-mode needs `fill` so both panes match heights.
        <div className="grid grid-cols-2 gap-3">
          <PreviewPane label="Vorschau (lokal)" fill>
            {renderClientPane(clientPreview, format, true)}
          </PreviewPane>
          <PreviewPane label="Vorschau (Server)" fill>
            {renderServerPane(serverPreview, format, pending, errMessage, true)}
          </PreviewPane>
        </div>
      ) : (
        // Single-pane: let the muted box hug the QR (no h-full / justify-
        // center). Earlier this was `fill`, which stretched the grey
        // background to the full PreviewPanel-card height — the QR sat
        // centred with empty space above, and on narrow cards the inner
        // aspect-square wrapper spilled below the rounded border.
        <PreviewPane label={t("section.code")}>
          {format === "pdf"
            ? renderServerPane(serverPreview, format, pending, errMessage, false)
            : renderClientPane(clientPreview, format, false, displayMm)}
        </PreviewPane>
      )}
      {/* Matrix-Info: zeigt die aktuelle Modul-Größe + ECC, damit
          ECC-Wechsel zwischen Stufen, die auf derselben QR-Version
          landen (z.B. MEDIUM ↔ QUARTILE bei kurzen URLs → beide V3,
          29×29), als „ist passiert" sichtbar bleiben — Mask + Recovery-
          Bits unterscheiden sich, die Größe nicht. */}
      <p className="mt-2 text-center text-[11px] tabular-nums text-muted-foreground">
        <Link
          href={
            `/${locale}/help/${
              locale === "de" ? "modulgroesse-ecc" : "module-size-ecc"
            }` as never
          }
          className="underline-offset-2 hover:text-foreground hover:underline"
          title={
            locale === "de"
              ? "Was bedeuten Version, Modul-Anzahl und ECC?"
              : "What do version, module count and ECC mean?"
          }
        >
          {matrixMeta ? `V${matrixMeta.version} · Mask ${matrixMeta.mask} · ` : ""}
          {currentMatrixModules - 8}×{currentMatrixModules - 8} Module · ECC {ecc}
        </Link>
      </p>
    </section>
  );
}
